import { useCallback, useEffect, useState } from 'react';

/** Browser permissions the softphone needs, in one vocabulary. */
export type PermissionState = 'granted' | 'denied' | 'prompt' | 'unsupported';

export async function microphoneState(): Promise<PermissionState> {
  if (!navigator.mediaDevices?.getUserMedia) return 'unsupported';
  try {
    const status = await navigator.permissions.query({ name: 'microphone' as PermissionName });
    return status.state;
  } catch {
    // Some browsers can't report it; they ask when a call starts.
    return 'prompt';
  }
}

export function notificationState(): PermissionState {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission === 'default' ? 'prompt' : Notification.permission;
}

/** Shows the browser's microphone prompt, then releases the microphone again. */
export async function requestMicrophone(): Promise<PermissionState> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    for (const track of stream.getTracks()) track.stop();
    return 'granted';
  } catch (error) {
    return (error as DOMException).name === 'NotAllowedError' ? 'denied' : 'prompt';
  }
}

export async function requestNotifications(): Promise<PermissionState> {
  if (!('Notification' in window)) return 'unsupported';
  const result = await Notification.requestPermission();
  return result === 'default' ? 'prompt' : result;
}

/** The current state of both, kept up to date when the user changes them in the browser. */
export function usePermissions() {
  const [microphone, setMicrophone] = useState<PermissionState>('prompt');
  const [notifications, setNotifications] = useState<PermissionState>(notificationState());
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    setMicrophone(await microphoneState());
    setNotifications(notificationState());
    setLoaded(true);
  }, []);

  useEffect(() => {
    void refresh();
    const watched: PermissionStatus[] = [];
    for (const name of ['microphone', 'notifications'] as PermissionName[]) {
      navigator.permissions
        ?.query({ name })
        .then((status) => {
          status.onchange = () => void refresh();
          watched.push(status);
        })
        .catch(() => undefined);
    }
    return () => {
      for (const status of watched) status.onchange = null;
    };
  }, [refresh]);

  return { microphone, notifications, loaded, refresh, setMicrophone, setNotifications };
}
